<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeLock from "@dsk/icons/LeLock.vue";
import LeProfile from "@dsk/icons/LeProfile.vue";
import { useField, useForm } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { ResponseError } from "@/api";
import { config } from "@/config";
import { ValidationHelper } from "@/helpers";
import { useUserStore } from "@/stores";
import { loginValidator } from "@/validation";

const props = defineProps<{
  backUrl: string;
}>();

const emit = defineEmits<{
  login: [redirectUrl: string];
}>();

const { language } = config.app;

const { t } = useI18n();
const { login } = useUserStore();

const { handleSubmit, errors } = useForm({ validationSchema: loginValidator });

const { value: username, meta: usernameMeta } = useField<string>("username");
const { value: password, meta: passwordMeta } = useField<string>("password");

const isSubmitting = ref<boolean>(false);

const onSubmit = handleSubmit(async (form) => {
  isSubmitting.value = true;

  try {
    const { redirectUrl } = await login(form, props.backUrl);
    emit("login", redirectUrl);
  } catch (e) {
    if (e instanceof ResponseError) {
      usernameMeta.valid = false;
      passwordMeta.valid = false;
      errors.value.password = t("validation.invalid-login");
    }
  } finally {
    isSubmitting.value = false;
  }
});
</script>

<template>
  <form class="le-user-login-form" @submit.prevent="onSubmit()">
    <div>
      <label for="username" class="le-user-login-form__field-label">
        {{ t("le-login.form.username.label") }}
      </label>
      <LeInput
        id="username"
        v-model="username"
        :placeholder="t('le-login.form.username.placeholder')"
        :has-error="ValidationHelper.displayError(usernameMeta)"
      >
        <template #iconBefore>
          <LeProfile role="presentation" class="le-user-login-form__input-icon" />
        </template>
      </LeInput>
      <small
        v-if="ValidationHelper.displayError(usernameMeta) && errors.username"
        class="le-user-login-form__validation-error"
      >
        {{ errors.username }}
      </small>
    </div>

    <div>
      <label for="password" class="le-user-login-form__field-label">
        {{ t("le-login.form.password.label") }}
      </label>
      <LeInput
        id="password"
        v-model="password"
        type="password"
        :placeholder="t('le-login.form.password.placeholder')"
        :has-error="ValidationHelper.displayError(passwordMeta)"
      >
        <template #iconBefore>
          <LeLock role="presentation" class="le-user-login-form__input-icon" />
        </template>
      </LeInput>
      <small
        v-if="ValidationHelper.displayError(passwordMeta) && errors.password"
        class="le-user-login-form__validation-error"
      >
        {{ errors.password }}
      </small>
    </div>

    <div class="le-user-login-form__forgot-password-container">
      <a
        :href="`/${language}/reset-password`"
        class="le-user-login-form__link le-user-login-form__forgot-password-link"
      >
        {{ t("le-login.forgot-password.link") }}
      </a>
    </div>

    <div class="le-user-login-form__button">
      <LeButton type="submit" size="large" :disabled="isSubmitting" full-width>
        {{ t("le-login.button.login") }}
      </LeButton>
    </div>
  </form>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-user-login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__field-label {
    .body-text("xs");
    color: @colors[@neutrals][200];
  }

  &__input-icon {
    color: @colors[@secondary][600];
  }

  &__validation-error {
    margin-left: 12px;
    .body-text("xs", @style: "italic");
    color: @colors[@error][600];
  }

  &__forgot-password-container {
    display: flex;
    width: 100%;
  }

  &__forgot-password-link {
    margin-left: auto;
    .body-text("s");
    .link();
    color: @colors[@neutrals][500];
  }

  &__button {
    #media-breakpoint-up(md, {
      margin-top: 14px;
    });
  }
}
</style>
